import { Fragment } from 'react';
import { Badge, InfoTip } from '@zarya/ui';
import { hasKey, t } from '@app/i18n';
import { identityValidProof } from './identityValidProof';
import type { CheckId, RuntimeStatus, TriEstado } from './Runtime.api';
import { EDGE_ROUTE, IDENTITY_VALID, check as checkVocab, checkIdsOf, keyTri, tri } from './runtimeVocab';
import s from './Runtime.module.css';

/**
 * Os oito checks de T15 (FR-090, FR-091, FR-132).
 *
 * Cada linha tem o ⓘ com a explicação e, quando falha ou o executor não sabe, o
 * motivo. `indeterminado` nunca conta como pronto (AC-38).
 *
 * # DE ONDE VEM O MOTIVO (aim-x7s)
 *
 * Os motivos que o próprio console escreve em `checks_reasons` saem em inglês, para quem lê a API. A tela não os
 * mostra: ela compõe a mesma frase pelos dois catálogos, pelo check e pelo valor (`consoleReason`). A frase do
 * servidor só aparece onde a tela não a pode compor: o «Identidade válida», cujo motivo é do executor, e a «API no
 * gateway», cujo motivo cita a API `a2a/<caminho>`, que a resposta não traz em outro campo.
 *
 * # CHECK DISPENSADO PELA POSTURA (P5)
 *
 * O que a postura do site não exige (`checks_not_required`) aparece **neutro**, com
 * «não exigido nesta postura» no lugar do motivo. Três decisões dentro desta:
 *
 *  - **não é vermelho**, porque não é falha: numa postura L1/L2 o produto nunca
 *    prometeu aquele vínculo, e pintar de vermelho o que não se exige treina quem
 *    opera a ignorar vermelho — o custo aparece no dia de uma falha de verdade;
 *  - **não some da lista**, porque o executor continua respondendo sobre ele, e
 *    esconder a leitura seria decidir pelo operador o que ele pode saber;
 *  - **o motivo do executor é substituído**, porque ele é uma INSTRUÇÃO
 *    («reconcilie o admission») que manda fazer o que o produto acabou de recusar.
 *
 * # «IDENTIDADE VÁLIDA» SEM A PERMISSÃO DE EXEC (aim-ivi.1, protótipo §1.1)
 *
 * O check vem do executor, que o prova pelo log do helper de identidade. Verde, a linha mostra as horas da prova
 * («renovado às 21:16 · usado no gateway às 21:40»). O `null` LIDO é «não verificável» (D2): badge neutro e o motivo
 * do executor. Desde o aim-ivi.3 (decisão do mrolim, 11/10/2026) ele conta, e deixa o Security Ready indeterminado:
 * a linha não diz mais «não conta no Security Ready», que fica só na «API no gateway». A chave ausente continua
 * «indeterminado», e conta.
 */

export interface ChecksListProps {
  status: RuntimeStatus;
}

/** Os checks com uma frase própria para o valor `null`; os outros dizem «indeterminado». */
const UNKNOWN_REASON = new Set<CheckId>(['release_matches', 'authorization_ready']);

/**
 * O motivo de um check que não é `true`, ou `null` quando não há motivo («sem motivo informado pelo executor»).
 *
 * Pela regra do mrolim (aim-mng, 11/10/2026): (1) o check ausente ou sem valor e sem motivo diz «o executor não
 * informou este check»; (2) o check com valor e sem motivo diz «sem motivo informado pelo executor»; (3) o motivo do
 * executor, quando existe, sempre vence.
 */
function consoleReason(id: CheckId, valor: TriEstado | undefined, status: RuntimeStatus): string | null {
  if (valor == null && status.error_code && id !== EDGE_ROUTE) {
    return t('runtimeStatus.check.notRead', { code: status.error_code });
  }
  if (id === IDENTITY_VALID || id === EDGE_ROUTE) {
    return status.checks_reasons?.[id] ?? (valor === false ? null : t('runtimeStatus.check.notReported'));
  }
  if (valor === false) {
    if (id === 'runtime_ready' && status.workload) {
      return t('runtimeStatus.check.runtime_ready.failedWithWorkload', { workload: status.workload });
    }
    return hasKey(`runtimeStatus.check.${id}.failed`) ? t(`runtimeStatus.check.${id}.failed`) : null;
  }
  if (valor === null) {
    return UNKNOWN_REASON.has(id) ? t(`runtimeStatus.check.${id}.unknown`) : t('runtimeStatus.check.unknown');
  }
  return t('runtimeStatus.check.notReported');
}

export function ChecksList({ status }: ChecksListProps) {
  const notRequired = new Set(status.checks_not_required ?? []);

  return (
    <ul className={s.checks}>
      {checkIdsOf(status.checks).map((id) => {
        const valor = status.checks[id];
        const dispensado = notRequired.has(id);
        const vocab = checkVocab(id);
        const unverifiable = id === IDENTITY_VALID && valor === null;
        const badge = unverifiable
          ? { rotulo: t('runtimeStatus.check.identity_valid.unverifiable'), tone: 'neutral' as const }
          : tri(dispensado ? 'indeterminado' : keyTri(valor));
        const motivo = valor === true ? null : consoleReason(id, valor, status);
        const proof = id === IDENTITY_VALID && valor === true ? identityValidProof(status) : [];
        return (
          <li
            key={id}
            className={s.check}
            data-testid={`check-${id}`}
            data-not-required={dispensado ? 'true' : undefined}
            data-unverifiable={unverifiable ? 'true' : undefined}
          >
            <span className={s.checkRotulo}>
              {vocab.rotulo}
              <InfoTip
                text={vocab.tip}
                label={t('runtimeStatus.check.about', { label: vocab.rotulo })}
                size="xs"
              />
            </span>
            <Badge tone={dispensado ? 'neutral' : badge.tone}>
              {dispensado ? t('sitePosture.checkNotRequiredBadge') : badge.rotulo}
            </Badge>
            <span className={s.checkMotivo}>
              {dispensado
                ? t('sitePosture.checkNotRequired')
                : valor === true
                  ? proof.length > 0
                    ? proof.map((part, index) => (
                        // O « · » é pontuação e se compõe aqui, entre as partes, e nunca no catálogo.
                        <Fragment key={part.kind}>
                          {index > 0 ? ' · ' : null}
                          <span data-testid={`identity-valid-${part.kind}`} data-at={part.at}>
                            {part.text}
                          </span>
                        </Fragment>
                      ))
                    : t('runtimeStatus.check.ok')
                  : (motivo ?? t('runtimeStatus.check.noReason'))}
              {/* D5: a linha existe para ser vista, e diz em voz alta que não pesa no badge. O «não verificável»
                  pesa desde o aim-ivi.3. */}
              {id === EDGE_ROUTE ? (
                <span data-testid="check-not-counted">{` · ${t('runtimeStatus.check.notCounted')}`}</span>
              ) : null}
            </span>
          </li>
        );
      })}
    </ul>
  );
}
